export interface PageOption {
  value: number;
  title: string;
}

export function buildPageOptions(totalCount: number, pageSize: number): PageOption[] {
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const options: PageOption[] = [];
  for (let page = 0; page < pageCount; page++) {
    const first = page * pageSize + 1;
    const last = Math.min((page + 1) * pageSize, totalCount);
    options.push({ value: page, title: `${first} - ${last}` });
  }

  return options;
}

export function filterByName<T>(items: T[], getName: (item: T) => string, nameFilter: string): T[] {
  const normalizedFilter = nameFilter.trim().toLocaleLowerCase();
  if (!normalizedFilter) {
    return items;
  }

  return items.filter((item) => getName(item).toLocaleLowerCase().indexOf(normalizedFilter) > -1);
}

/**
 * Put the suggestions that match a search term before the other results.
 *
 * @param results Items returned by the search, in server order.
 * @param suggestions Items to show first when their name matches the term.
 * @param searchTerm Text typed by the user.
 * @param getName Returns the display name of an item.
 * @returns The matching suggestions followed by the results that are not suggestions.
 */
export function pinSuggestionsToTop<T extends { id?: string }>(
  results: T[],
  suggestions: T[],
  searchTerm: string,
  getName: (item: T) => string
): T[] {
  const matchingSuggestions = filterByName(suggestions, getName, searchTerm);
  const suggestionIds = new Set(matchingSuggestions.map((suggestion) => suggestion.id));

  return matchingSuggestions.concat(results.filter((result) => !suggestionIds.has(result.id)));
}

export function visibleSlice<T>(items: T[], currentPage: number, pageSize: number): T[] {
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const page = Math.max(Math.min(currentPage, pageCount - 1), 0);
  const offset = page * pageSize;

  return items.slice(offset, offset + pageSize);
}
